import { useState } from 'react';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [agreeToDeals, setAgreeToDeals] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!agreeToDeals) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const formData = new URLSearchParams();
    formData.append('email', email);
    formData.append('name', name);
    formData.append('phone', phone);
    formData.append('agree_to_deals', agreeToDeals ? 'Yes' : 'No');

    try {
      const response = await fetch('https://readdy.ai/api/form/d67jlklbiueuoqm1r550', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setName('');
        setPhone('');
        setAgreeToDeals(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#F8F6F3] to-[#EDF5F0] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="flex items-center gap-3">
                <img 
                  src="https://public.readdy.ai/ai/img_res/e3e9434f-1ac5-481d-ae0c-2ba4a3a5d65b.png" 
                  alt="Prop Invest Logo" 
                  className="h-16 w-auto object-contain"
                />
                <span className="text-3xl font-bold text-[#2C5F4F]">Prop Invest</span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight text-gray-900">
                Discover Premium <span className="text-[#2C5F4F]">Property Investments</span> That Build Wealth
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Prop Invest gives you access to exclusive investment opportunities in prime UK properties with verified returns and professional management.
              </p>
              <a 
                href="#signup" 
                className="inline-flex items-center gap-3 bg-[#2C5F4F] text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-[#234a3d] transition-all hover:-translate-y-1 shadow-lg whitespace-nowrap cursor-pointer"
              >
                Get Started Today
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>

            {/* Right - Property Cards */}
            <div className="relative h-[500px] hidden lg:block">
              <div className="absolute top-0 left-0 w-72 transform -rotate-3 transition-transform hover:rotate-0">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/986ff201f0638aa59b24724c4d3f7179.jpg" 
                    alt="Property Investment" 
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-4 bg-gradient-to-t from-black/60 to-transparent absolute bottom-0 left-0 right-0">
                    <p className="text-white font-semibold flex items-center gap-2">
                      <i className="ri-map-pin-line"></i>
                      London, UK
                    </p>
                    <p className="text-white text-sm">12% Annual Return</p>
                  </div>
                </div>
              </div>

              <div className="absolute top-12 right-0 w-72 transform rotate-2 z-10 transition-transform hover:rotate-0">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20apartment%20complex%20in%20Manchester%20Salford%20Quays%20waterfront%20with%20red%20brick%20and%20glass%20facade%20contemporary%20British%20urban%20development%20along%20the%20canal%20with%20cloudy%20sky&width=400&height=300&seq=ukhero2&orientation=landscape" 
                    alt="Property Investment" 
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-4 bg-gradient-to-t from-black/60 to-transparent absolute bottom-0 left-0 right-0">
                    <p className="text-white font-semibold flex items-center gap-2">
                      <i className="ri-map-pin-line"></i>
                      Manchester, UK
                    </p>
                    <p className="text-white text-sm">15% Annual Return</p>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-0 left-16 w-72 transform -rotate-2 transition-transform hover:rotate-0">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Georgian%20townhouses%20in%20Birmingham%20Jewellery%20Quarter%20with%20red%20brick%20facades%20and%20sash%20windows%20traditional%20English%20Midlands%20architecture%20with%20iron%20lamp%20posts%20and%20cobblestone%20street&width=400&height=300&seq=ukhero3&orientation=landscape" 
                    alt="Property Investment" 
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-4 bg-gradient-to-t from-black/60 to-transparent absolute bottom-0 left-0 right-0">
                    <p className="text-white font-semibold flex items-center gap-2">
                      <i className="ri-map-pin-line"></i>
                      Birmingham, UK
                    </p>
                    <p className="text-white text-sm">10% Annual Return</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Property Investment */}
      <section className="bg-[#FAFAF8] py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Smart Property Investment Made Simple
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed max-w-xl">
                At Prop Invest, we specialize in identifying and vetting premium property investment opportunities across the UK. Our expert team conducts thorough due diligence on every property, ensuring you have access to only the most promising investments with strong potential returns and minimal risk.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed max-w-xl">
                Whether you're a seasoned investor or just starting your property investment journey, Prop Invest provides comprehensive support, transparent reporting, and professional property management services to maximize your returns.
              </p>

              {/* Stats */}
              <div className="flex gap-12 pt-8">
                <div>
                  <p className="text-5xl font-bold text-[#2C5F4F]">£2.4M</p>
                  <p className="text-sm text-gray-500 mt-2">Total Investments</p>
                </div>
                <div>
                  <p className="text-5xl font-bold text-[#2C5F4F]">94%</p>
                  <p className="text-sm text-gray-500 mt-2">Client Satisfaction</p>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-3">
              <div className="relative rounded-[32px] overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20London%20skyline%20with%20the%20Shard%20and%20Tower%20Bridge%20showing%20mix%20of%20historic%20and%20modern%20architecture%20Thames%20river%20winding%20through%20the%20city%20British%20capital%20cityscape%20at%20golden%20hour&width=800&height=600&seq=ukabout1&orientation=landscape" 
                  alt="Property Investment Portfolio" 
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-6 left-6">
                  <span className="bg-white px-4 py-2 rounded-full text-sm font-semibold text-gray-900 flex items-center gap-2 shadow-lg">
                    <i className="ri-map-pin-line text-[#2C5F4F]"></i>
                    London, UK
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-8">
                  <p className="text-white text-xl font-semibold">Premium Residential Development</p>
                  <p className="text-white/90 text-lg mt-1">Expected ROI: 14% per annum</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#EDF5F0] py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl lg:text-5xl font-bold text-center text-gray-900 mb-16">
            Why Invest With Prop Invest
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Benefit 1 */}
            <div className="bg-[#1A1A1A] rounded-3xl p-8 text-white relative overflow-hidden">
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#A8D5BA] rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-3xl text-[#1A1A1A]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Vetted Opportunities</h3>
                <p className="text-white/70 leading-relaxed mb-6">
                  Every property undergoes rigorous due diligence by our expert team. We verify legal compliance, assess market potential, and ensure all investments meet our strict quality standards before presenting them to you.
                </p>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-64 opacity-20">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20property%20investment%20verification%20concept%20with%20checkmarks%20and%20quality%20assurance%20symbols%20minimalist%20professional%20background&width=400&height=300&seq=benefit1&orientation=landscape" 
                  alt="Vetted Properties" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Benefit 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-[#2C5F4F] rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-center mb-4 text-gray-900">Strong Returns</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-600">
                  <i className="ri-check-line text-[#2C5F4F] text-xl mt-1"></i>
                  <span>Average 10-15% annual returns</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <i className="ri-check-line text-[#2C5F4F] text-xl mt-1"></i>
                  <span>Transparent fee structure</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <i className="ri-check-line text-[#2C5F4F] text-xl mt-1"></i>
                  <span>Regular performance updates</span>
                </li>
              </ul>
            </div>

            {/* Benefit 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-[#2C5F4F] rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-customer-service-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-center mb-4 text-gray-900">Full Support</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-600">
                  <i className="ri-check-line text-[#2C5F4F] text-xl mt-1"></i>
                  <span>Dedicated account manager</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <i className="ri-check-line text-[#2C5F4F] text-xl mt-1"></i>
                  <span>Professional property management</span>
                </li>
                <li className="flex items-start gap-3 text-gray-600">
                  <i className="ri-check-line text-[#2C5F4F] text-xl mt-1"></i>
                  <span>24/7 investor portal access</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Deals */}
      <section id="deals" className="bg-white py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Current Investment Opportunities
            </h2>
            <p className="text-gray-500 text-sm max-w-xs hidden lg:block">
              All properties are fully vetted and ready for investment
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Deal 1 */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 group">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=Elegant%20Edwardian%20mansion%20block%20apartments%20in%20Chelsea%20London%20with%20ornate%20balconies%20and%20cream%20colored%20facade%20traditional%20British%20luxury%20residential%20property%20with%20wrought%20iron%20details&width=400&height=350&seq=ukdeal1&orientation=portrait" 
                  alt="London Property" 
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-white px-3 py-1 rounded-full text-xs font-semibold text-gray-900 flex items-center gap-1 shadow-md">
                    <i className="ri-map-pin-line text-[#2C5F4F]"></i>
                    Central London
                  </span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Luxury Apartment Development</h3>
                <div className="space-y-2">
                  <p className="text-gray-600 flex justify-between">
                    <span>Minimum Investment:</span>
                    <strong className="text-gray-900">£50,000</strong>
                  </p>
                  <p className="text-gray-600 flex justify-between">
                    <span>Projected Return:</span>
                    <strong className="text-[#2C5F4F]">14% p.a.</strong>
                  </p>
                  <p className="text-gray-600 flex justify-between">
                    <span>Investment Term:</span>
                    <strong className="text-gray-900">3 years</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Deal 2 */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 group">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20glass%20office%20building%20in%20Manchester%20Spinningfields%20business%20district%20with%20reflective%20facade%20contemporary%20British%20commercial%20architecture%20with%20pedestrian%20plaza%20and%20urban%20landscaping&width=400&height=350&seq=ukdeal2&orientation=portrait" 
                  alt="Manchester Property" 
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-white px-3 py-1 rounded-full text-xs font-semibold text-gray-900 flex items-center gap-1 shadow-md">
                    <i className="ri-map-pin-line text-[#2C5F4F]"></i>
                    Manchester
                  </span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Commercial Property Portfolio</h3>
                <div className="space-y-2">
                  <p className="text-gray-600 flex justify-between">
                    <span>Minimum Investment:</span>
                    <strong className="text-gray-900">£75,000</strong>
                  </p>
                  <p className="text-gray-600 flex justify-between">
                    <span>Projected Return:</span>
                    <strong className="text-[#2C5F4F]">12% p.a.</strong>
                  </p>
                  <p className="text-gray-600 flex justify-between">
                    <span>Investment Term:</span>
                    <strong className="text-gray-900">5 years</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Deal 3 */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 group">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=New%20build%20housing%20estate%20in%20Birmingham%20suburbs%20with%20semi%20detached%20homes%20red%20brick%20construction%20traditional%20British%20residential%20development%20with%20front%20gardens%20and%20driveways&width=400&height=350&seq=ukdeal3&orientation=portrait" 
                  alt="Birmingham Property" 
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-white px-3 py-1 rounded-full text-xs font-semibold text-gray-900 flex items-center gap-1 shadow-md">
                    <i className="ri-map-pin-line text-[#2C5F4F]"></i>
                    Birmingham
                  </span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Residential Housing Development</h3>
                <div className="space-y-2">
                  <p className="text-gray-600 flex justify-between">
                    <span>Minimum Investment:</span>
                    <strong className="text-gray-900">£35,000</strong>
                  </p>
                  <p className="text-gray-600 flex justify-between">
                    <span>Projected Return:</span>
                    <strong className="text-[#2C5F4F]">11% p.a.</strong>
                  </p>
                  <p className="text-gray-600 flex justify-between">
                    <span>Investment Term:</span>
                    <strong className="text-gray-900">4 years</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Email Signup */}
      <section id="signup" className="bg-[#F5F1EB] py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Never Miss An Investment Opportunity
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Join 2,000+ Prop Invest members receiving exclusive deal alerts weekly
          </p>

          <form 
            onSubmit={handleSubmit} 
            className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 space-y-6"
            data-readdy-form
            id="investment-signup-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="text-left">
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5F4F] focus:border-transparent text-base"
                  placeholder="John Smith"
                />
              </div>

              <div className="text-left">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5F4F] focus:border-transparent text-base"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="text-left">
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5F4F] focus:border-transparent text-base"
                placeholder="+44 7700 900000"
              />
            </div>

            <div className="text-left">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="agree_to_deals"
                  checked={agreeToDeals}
                  onChange={(e) => setAgreeToDeals(e.target.checked)}
                  required
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#2C5F4F] focus:ring-[#2C5F4F] cursor-pointer"
                />
                <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  I agree to receive investment deals and property opportunities from Prop Invest via email. <span className="text-red-500">*</span>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !agreeToDeals}
              className="w-full bg-[#3D2817] text-white py-4 rounded-full text-lg font-semibold hover:bg-[#2C5F4F] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Submitting...' : 'Get Investment Alerts'}
              <i className="ri-arrow-right-line text-xl"></i>
            </button>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl">
                <p className="font-semibold">Success! You're now subscribed.</p>
                <p className="text-sm">Check your email for exclusive investment opportunities.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl">
                <p className="font-semibold">Something went wrong.</p>
                <p className="text-sm">Please try again or contact us directly.</p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#0F1419] text-white py-16 mx-6 mb-6 rounded-[32px]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            {/* Column 1 */}
            <div>
              <h3 className="text-3xl font-bold mb-6">Start Investing with Prop Invest Today</h3>
              <p className="text-gray-400">
                Join thousands of investors building wealth through premium UK property investments.
              </p>
            </div>

            {/* Column 2 */}
            <div>
              <h4 className="text-base font-semibold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="text-gray-400 flex items-center gap-2">
                  <i className="ri-mail-line text-xl"></i>
                  info@propinvest.co.uk
                </li>
                <li className="text-gray-400 flex items-center gap-2">
                  <i className="ri-phone-line text-xl"></i>
                  +44 20 7123 4567
                </li>
                <li className="text-gray-400 flex items-center gap-2">
                  <i className="ri-map-pin-line text-xl"></i>
                  London, United Kingdom
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">© 2025 Prop Invest. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
